import { logCoreUsage } from 'owa-analytics';
import { isFeatureEnabled } from 'owa-feature-flags';

/**
 * Defines all possible entry points that can trigger the opening of the BizChat Side Pane.
 *
 * These string literals are used for telemetry logging to track user engagement patterns
 * and feature adoption across different UI surfaces. The values must remain static to
 * prevent telemetry cardinality explosion and ensure consistent analytics reporting.
 *
 * When adding a new BizChat entry point:
 * 1. Add the new string literal to this union type
 * 2. Use descriptive naming that clearly identifies the UI element or user action
 * 4. Ensure the name is unique and won't conflict with existing entries
 *
 * @example
 * // Usage in a component
 * const handleCopilotClick = () => {
 *   logBizChatSidePaneEntrypoint('SuiteHeaderButton');
 *   // ... open BizChat logic
 * };
 */
export type BizChatSidePaneSources =
    | 'SuiteHeaderButton'
    | 'SummarizeButtonEntrypoint'
    | 'CopilotCommandCenter_MenuItem'
    | 'CopilotCommandCenter_SplitButtonPrimaryAction'
    | 'CopilotCommandCenter_ChatPromptInput'
    | 'UserHighlightingEntrypoint'
    | 'AutoOpenFRE' // Old AppChat AutoOpen entrypoint, will be removed soon.
    | 'StickyOpen'
    | 'UserHighlightNativeContextMenu'
    | 'RecipientContextMenuEntrypoint'
    | 'SummarizeLinkPopup'
    | 'PodcastLinkPopup'
    | 'SearchReadingPaneEntrypoint'
    | 'Addins'
    | 'SearchReadingPaneSuggestionEntrypoint'
    | 'ReadWithMe'
    | 'ImageButtonEntrypoint'
    | 'RecipientSuggestionsFooter'
    | 'NSSmartTimeEntrypoint'
    | 'CopilotCatchUpRollup'
    | 'SearchPlusChatEntryPointClick_SameSearchSession'
    | 'AskResearcher'
    | 'CopilotTeachingPopover'
    | 'BizChatSidePaneAutoOpen';

/**
 * @param source The entry point source that triggered the BizChat Side Pane opening.
 * @param isFlexPaneAlreadyOpen Indicates whether the BizChat Side Pane is already open,
 *                              used to understand full usage for features that can populate the input box.
 */
export function logBizChatSidePaneEntrypoint(
    source: BizChatSidePaneSources,
    isFlexPaneAlreadyOpen?: boolean
): void {
    // TEMPORARY: This function currently handles both BizChat and legacy AppChat entrypoints
    // during the transition period. We're consolidating `logCopilotChatEntrypoint` functionality
    // into this function as the mon-copilot-bizchat feature approaches worldwide rollout.
    // Once fully migrated, this function will be the single source of truth for all Copilot
    // chat entrypoint logging, and the legacy AppChat dependency of 'OutlookCopilotChat_Entrypoints' will be removed.
    isFeatureEnabled('mon-copilot-bizchat')
        ? logCoreUsage('OutlookCopilotChat_BizChatSidePaneEntrypoints', {
              source,
              isFlexPaneAlreadyOpen: isFlexPaneAlreadyOpen ?? false,
          })
        : logCoreUsage('OutlookCopilotChat_Entrypoints', { source });
}
